/**
 * LabelAtlas — TODOS os nomes dos jogadores numa única chamada de desenho.
 *
 * O PROBLEMA: cada jogador tinha o seu próprio sprite com a sua própria textura.
 * Textura própria significa draw call próprio, e draw call é o que derruba o fps
 * numa GPU integrada — o custo é de CPU/driver, não de pixel. Medido no notebook
 * alvo: ~30 µs por chamada, ou seja 500 jogadores = ~15 ms por quadro só de
 * rótulos, metade do orçamento de um quadro a 30 fps.
 *
 * A SOLUÇÃO: uma PÁGINA de atlas (canvas 2048×2048) dividida em células fixas, e
 * um único `Mesh` com geometria instanciada por página.
 *
 * CÉLULA ≠ INSTÂNCIA. São dois recursos independentes, e é isso que permite
 * COMPARTILHAR textura entre jogadores homônimos:
 *   • a CÉLULA guarda a pílula pintada, e é indexada por (texto + cor). Numa
 *     população real de 500 jogadores há ~33 primeiros nomes distintos — então
 *     500 pinturas de pílula viram 33, e o atlas inteiro cabe numa página só
 *     (1 draw call em vez de 3). A célula é liberada quando o último jogador
 *     daquele nome sai (contagem de referências).
 *   • a INSTÂNCIA é por jogador: carrega a posição de mundo e o tamanho. Vários
 *     jogadores apontam para o mesmo retângulo de UV da mesma célula.
 *
 * QUEM NÃO CABE FICA DE FORA: uma célula tem tamanho fixo, e o texto de um
 * jogador exibindo MENSAGEM muda a cada caractere. Esses ficam no caminho antigo,
 * um sprite individual — são poucos ao mesmo tempo (alguns draw calls, não 500),
 * a animação de digitação continua exatamente como era, e o atlas não sofre com
 * células entrando e saindo 22 vezes por segundo.
 *
 * ATUALIZAÇÃO PARCIAL: reenviar a página inteira a cada célula pintada seriam
 * 16 MB por vez. Então a pílula é pintada num canvas de rascunho do tamanho da
 * célula e copiada com `renderer.copyTextureToTexture`, que faz um
 * `texSubImage2D` só naquele retângulo. O canvas da página é mantido em dia junto
 * porque é dele que o three re-sobe a textura se o contexto WebGL for perdido —
 * cenário real num telão de 8 horas. Sem `renderer` (testes headless), cai para o
 * reenvio inteiro.
 *
 * BILLBOARD: o rótulo tem que encarar a câmera como um sprite. Em vez de um
 * ShaderMaterial cru, isto usa um MeshBasicMaterial com `onBeforeCompile` — assim
 * a decodificação de cor sRGB e o resto vêm prontos do three. O patch só troca a
 * posição do vértice (expande o quad no espaço da câmera) e o UV (recorta a
 * célula da instância).
 */
import * as THREE from 'three';
import { probes, P } from '../perfProbes.js';
import { paintLabel, LABEL_SCALE } from './makeLabel.js';

const PAGE = 2048;    // lado da página de atlas (px)
const CELL_W = 256;   // célula: cabe a pílula do nome no tamanho MÁXIMO que makeLabel gera
// ALTURA = as MAX_LINES inteiras, não duas delas. Com 96 px a célula comportava só 2
// linhas (2 linhas = 84,0 px; 3 linhas = 116,4 px na resolução atual), e todo jogador cujo
// nome completo quebrava em 3 linhas era EXPULSO do atlas para um sprite individual — pela
// sessão inteira, porque o nome não muda. MEDIDO: 12–31% dos jogadores, ou seja dezenas de
// chamadas de desenho permanentes num telão de 8 h, exatamente o custo que o atlas existe
// para eliminar. 128 px cabe as 3 linhas com folga e divide 2048 exatamente (16 fileiras).
// Custo: 128 células por página em vez de 168 — páginas são baratas perto de um draw call
// por jogador, e uma população com muitos nomes distintos agora paga 1 chamada por página
// em vez de 1 por pessoa.
const CELL_H = 128;
const COLS = Math.floor(PAGE / CELL_W); // 8
const ROWS = Math.floor(PAGE / CELL_H); // 16
const CELLS = COLS * ROWS;              // 128 texturas de nome distintas por página
// A GEOMETRIA DA CÉLULA É EXPORTADA de propósito: o harness verificava os UVs com uma
// cópia própria destes números e passou a acusar 168 falhas quando CELL_H mudou aqui.
// Verificação que duplica a constante que deveria verificar não verifica nada.
export const ATLAS_GEO = { PAGE, CELL_W, CELL_H, COLS, ROWS, CELLS };
// Poda das páginas vazias (ver _podarPaginas). Verifica a cada N flushes, mas o prazo é
// de RELÓGIO: a página tem que passar PAGINA_VAZIA_MS inteiros vazia, sem interrupção.
//
// MEDIDO: com a população indo e voltando em torno da fronteira de 168 nomes a cada 80 s
// (um grupo entrando e saindo, o ritmo do evento), um prazo de ~10 s fazia o atlas
// destruir e recriar uma página 45 VEZES POR HORA — 32 MB de canvas + textura em cada
// ciclo, engasgo periódico garantido numa placa integrada. O prazo longo cobre o vaivém
// normal e ainda devolve a memória de um pico que de fato passou.
const PODA_A_CADA = 120;                // ~2 s a 60 fps (só a CADÊNCIA da verificação)
const PAGINA_VAZIA_MS = 5 * 60 * 1000;  // 5 min vazia, ininterruptos, antes de devolver
// Instâncias por página. Muito maior que CELLS de propósito: com nomes repetidos,
// uma página só pode atender a população inteira. Custo: ~74 KB de atributos.
const INSTANCES = 2048;

const _dstPos = new THREE.Vector2();
const _size = { x: 0, y: 0 };

/** Cabe numa célula? (o chamador decide entre atlas e sprite individual) */
export function fitsAtlasCell(m) {
  return m.w <= CELL_W && m.h <= CELL_H;
}

export class LabelAtlas {
  /**
   * @param {THREE.Object3D} parent      grupo dos rótulos (herda o liga/desliga)
   * @param {THREE.WebGLRenderer} renderer opcional — habilita o upload parcial
   */
  constructor(parent, renderer) {
    this.parent = parent;
    this.renderer = renderer || null;
    this.pages = [];
    this._ciclos = 0; // contador de flushes — cadência da poda de páginas vazias
    this._semCopiaParcial = false; // upload parcial falhou → reenvia a página (ver _upload)
    this.cells = new Map(); // chave (texto|cor) → célula compartilhada
    // canvas de rascunho: a pílula é pintada aqui e copiada para a célula
    this._scratch = document.createElement('canvas');
    this._scratch.width = CELL_W;
    this._scratch.height = CELL_H;
    this._scratchCtx = this._scratch.getContext('2d');
    this._scratchTex = new THREE.CanvasTexture(this._scratch);
    this._scratchTex.flipY = false;
    this._scratchTex.generateMipmaps = false;
    this._scratchTex.minFilter = THREE.LinearFilter;
    this._scratchTex.colorSpace = THREE.SRGBColorSpace;
  }

  /** Nº de páginas vivas = nº de draw calls dos nomes. */
  get pageCount() {
    return this.pages.length;
  }

  /** Nº de texturas de nome distintas em uso (diagnóstico). */
  get cellCount() {
    return this.cells.size;
  }

  _newPage() {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = PAGE;
    const ctx = canvas.getContext('2d');
    const tex = new THREE.CanvasTexture(canvas);
    // flipY=false: a linha 0 do canvas vira v=0. Assim o `texSubImage2D` do
    // upload parcial escreve na orientação natural, sem inverter o retângulo.
    tex.flipY = false;
    // sem mipmap: (a) regerar a pirâmide de uma página 2048² a cada célula pintada
    // seria caríssimo; (b) num atlas os níveis altos misturariam células vizinhas,
    // e rótulos distantes começariam a sangrar uns nos outros.
    tex.generateMipmaps = false;
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.colorSpace = THREE.SRGBColorSpace;

    // quad unitário centrado + atributos por instância
    const base = new THREE.PlaneGeometry(1, 1);
    const geo = new THREE.InstancedBufferGeometry();
    geo.index = base.index;
    geo.setAttribute('position', base.attributes.position);
    geo.setAttribute('uv', base.attributes.uv);
    const iPos = new THREE.InstancedBufferAttribute(new Float32Array(INSTANCES * 3), 3);
    const iSize = new THREE.InstancedBufferAttribute(new Float32Array(INSTANCES * 2), 2);
    const iUv = new THREE.InstancedBufferAttribute(new Float32Array(INSTANCES * 4), 4);
    iPos.setUsage(THREE.DynamicDrawUsage);
    iSize.setUsage(THREE.DynamicDrawUsage);
    iUv.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('iPos', iPos);
    geo.setAttribute('iSize', iSize);
    geo.setAttribute('iUv', iUv);
    geo.instanceCount = 0;

    const mat = new THREE.MeshBasicMaterial({
      map: tex, transparent: true, depthWrite: false, depthTest: false,
      fog: false, toneMapped: false
    });
    mat.onBeforeCompile = (shader) => {
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', `#include <common>
        attribute vec3 iPos;
        attribute vec2 iSize;
        attribute vec4 iUv;`)
        // recorta a célula desta instância (sobrescreve o vMapUv do chunk padrão)
        .replace('#include <uv_vertex>', `#include <uv_vertex>
        vMapUv = iUv.xy + uv * iUv.zw;`)
        // BILLBOARD: leva o CENTRO para o espaço da câmera e expande o quad ali,
        // então ele encara a tela sempre — é o mesmo que um Sprite faz.
        .replace('#include <project_vertex>', `
        vec4 mvPosition = modelViewMatrix * vec4( iPos, 1.0 );
        mvPosition.xy += position.xy * iSize;
        gl_Position = projectionMatrix * mvPosition;`);
    };

    const mesh = new THREE.Mesh(geo, mat);
    mesh.name = 'labelAtlas' + this.pages.length;
    mesh.renderOrder = 999;              // igual ao sprite: acima de tudo
    mesh.frustumCulled = false;          // a caixa da geometria é só o quad unitário
    mesh.matrixAutoUpdate = false;       // fica na origem; a posição é por instância
    this.parent.add(mesh);

    const freeCells = [];
    for (let i = CELLS - 1; i >= 0; i--) freeCells.push(i);
    const page = {
      canvas, ctx, tex, mesh, geo, iPos, iSize, iUv,
      freeCells, freeInst: [], instHigh: 0, instLo: -1, instHiDirty: -1, uvLo: -1, uvHi: -1, uploaded: false,
      vaziaDesde: 0 // instante em que ficou 100% vazia (ver _podarPaginas)
    };
    this.pages.push(page);
    return page;
  }

  /**
   * Reserva o que um rótulo precisa: a CÉLULA da textura (compartilhada com quem
   * tiver o mesmo `key`) e uma INSTÂNCIA própria. Devolve o handle ou null se não
   * houver espaço.
   *
   * @param {string} key chave da textura — texto + cor
   * @param {object} m   métricas de measureLabel (só usadas se a célula for nova)
   */
  acquire(key, m, color) {
    let entry = this.cells.get(key);
    const nova = !entry;
    if (!entry) {
      entry = this._paintCell(key, m, color);
      if (!entry) return null;
    }
    const page = entry.page;
    const inst = page.freeInst.length ? page.freeInst.pop() : page.instHigh++;
    if (inst >= INSTANCES) {
      page.instHigh = INSTANCES;
      // A página está cheia de INSTÂNCIAS, mas a célula recém-pintada já saiu de
      // `freeCells` e entrou em `cells` com refs=0. Ninguém recebe handle, então
      // nada a devolve nunca: cada nome novo tentado com a página lotada queimava
      // uma célula PARA SEMPRE. Num telão de 8 h isso enche a página de células
      // órfãs e força páginas novas (16 MB cada) para uma cidade do mesmo tamanho.
      if (nova) { page.freeCells.push(entry.cell); this.cells.delete(key); }
      return null;
    }
    if (page.instHigh > page.geo.instanceCount) page.geo.instanceCount = page.instHigh;
    entry.refs++;
    // a instância aponta para o retângulo da célula compartilhada
    const a = page.iUv.array;
    a[inst * 4] = entry.u0;
    a[inst * 4 + 1] = entry.v0;
    a[inst * 4 + 2] = entry.du;
    a[inst * 4 + 3] = entry.dv;
    // entra na faixa suja do FLUSH (não sobe sozinho aqui): needsUpdate sem faixa
    // reenviaria as 2048×4 (32 KB) por jogador que entra, e addUpdateRange direto sem
    // consumo acumulava um range por spawn enquanto os rótulos estivessem ocultos
    // (a tela /me esconde todos — o array de ranges crescia sem teto com o churn).
    if (page.uvLo < 0 || inst < page.uvLo) page.uvLo = inst;
    if (inst > page.uvHi) page.uvHi = inst;
    return { page, inst, entry, key };
  }

  /** Pinta uma célula nova para `key`. null se todas as páginas estiverem cheias. */
  _paintCell(key, m, color) {
    let page = null;
    for (const p of this.pages) if (p.freeCells.length) { page = p; break; }
    if (!page) page = this._newPage();
    const cell = page.freeCells.pop();
    const cx = (cell % COLS) * CELL_W;
    const cy = ((cell / COLS) | 0) * CELL_H;
    const ox = (CELL_W - m.w) / 2; // pílula centrada na célula: a margem transparente
    const oy = (CELL_H - m.h) / 2; // garante que a filtragem bilinear não pegue a vizinha

    const s = this._scratchCtx;
    s.clearRect(0, 0, CELL_W, CELL_H);
    paintLabel(s, ox, oy, m, color);
    page.ctx.clearRect(cx, cy, CELL_W, CELL_H);
    page.ctx.drawImage(this._scratch, cx, cy); // mantém a imagem-fonte em dia
    this._upload(page, cx, cy);

    // retângulo de UV da PÍLULA (não da célula). v cresce para baixo no canvas
    // (flipY=false), e o quad tem uv.y=0 embaixo → dv negativo.
    const entry = {
      page, cell, refs: 0, key,
      u0: (cx + ox) / PAGE,
      v0: (cy + oy + m.h) / PAGE,
      du: m.w / PAGE,
      dv: -m.h / PAGE,
      w: m.w * LABEL_SCALE,
      h: m.h * LABEL_SCALE
    };
    this.cells.set(key, entry);
    return entry;
  }

  /** Tamanho da pílula em unidades de mundo `{ x, y }` (objeto reaproveitado). */
  sizeOf(h) {
    _size.x = h.entry.w;
    _size.y = h.entry.h;
    return _size;
  }

  /** Devolve a instância e, se foi a última daquele nome, a célula da textura. */
  release(h) {
    if (!h) return;
    const { page, inst, entry } = h;
    page.iSize.array[inst * 2] = 0;
    page.iSize.array[inst * 2 + 1] = 0;
    // entra na faixa suja do próximo flush (não precisa subir sozinho agora):
    // instância liberada só é redesenhada quando outro jogador a reusar
    if (page.instLo < 0 || inst < page.instLo) page.instLo = inst;
    if (inst > page.instHiDirty) page.instHiDirty = inst;
    page.freeInst.push(inst);
    if (--entry.refs <= 0) {
      page.freeCells.push(entry.cell);
      this.cells.delete(entry.key);
    }
  }

  /** Posição de mundo e tamanho (já com fontScale e escala do avatar) da instância. */
  write(h, x, y, z, w, hgt) {
    probes.begin(P.atlas);
    const { page, inst } = h;
    const p = page.iPos.array;
    const s = page.iSize.array;
    // valores idênticos aos do quadro anterior → nada a escrever nem a subir. Vale
    // para os rótulos de avatares PARADOS com pilha assentada (posição, elevação e
    // tamanho estáveis) — a comparação é exata, então o pulo é invisível.
    if (p[inst * 3] === x && p[inst * 3 + 1] === y && p[inst * 3 + 2] === z &&
        s[inst * 2] === w && s[inst * 2 + 1] === hgt) { probes.end(P.atlas); return; }
    p[inst * 3] = x; p[inst * 3 + 1] = y; p[inst * 3 + 2] = z;
    s[inst * 2] = w; s[inst * 2 + 1] = hgt;
    if (page.instLo < 0 || inst < page.instLo) page.instLo = inst;
    if (inst > page.instHiDirty) page.instHiDirty = inst;
    probes.end(P.atlas);
  }

  /** Sobe só a faixa de instâncias que mudou (antes: o array INTEIRO de 2.048
   *  slots — 40 KB/quadro — mesmo com ~500 vivos e mesmo sem mudança nenhuma). */
  /**
   * DEVOLVE A MEMÓRIA DAS PÁGINAS QUE ESVAZIARAM. Uma página custa 2048×2048×4 = 16 MB de
   * canvas em RAM mais 16 MB de textura em VRAM, e nada nunca as removia: um pico de
   * público criava páginas que ficavam residentes — e desenhando — pelo resto do evento.
   * MEDIDO: um pico de 700 pessoas numa cidade que volta a 150 deixava 5 páginas, 3 delas
   * 100% vazias = 80 MB de RAM + 80 MB de VRAM presos por horas numa GPU de telão.
   *
   * O gatilho é baixo (200 nomes compostos já passam de uma página), então a folga
   * importa: só libera depois de a página ficar vazia por várias passadas seguidas, e
   * nunca a última — senão o atlas ficaria criando e destruindo 16 MB a cada 168 nomes.
   *
   * É seguro remover: o handle guarda a REFERÊNCIA da página, não um índice, e uma página
   * com todas as células livres não tem entrada viva (logo, nenhuma instância em uso).
   */
  _podarPaginas() {
    if (this.pages.length <= 1) return;
    const agora = performance.now();
    for (let i = this.pages.length - 1; i >= 1; i--) {
      const p = this.pages[i];
      if (p.freeCells.length !== CELLS) { p.vaziaDesde = 0; continue; }
      if (!p.vaziaDesde) { p.vaziaDesde = agora; continue; }
      if (agora - p.vaziaDesde < PAGINA_VAZIA_MS) continue;
      this.parent.remove(p.mesh);
      p.tex.dispose();
      p.geo.dispose();
      p.mesh.material.dispose();
      p.canvas.width = p.canvas.height = 0; // devolve o bitmap ao browser
      this.pages.splice(i, 1);
    }
  }

  flush() {
    probes.begin(P.atlas);
    if (++this._ciclos % PODA_A_CADA === 0) this._podarPaginas();
    for (const p of this.pages) {
      if (p.instLo >= 0) {
        const n = p.instHiDirty - p.instLo + 1;
        p.iPos.clearUpdateRanges();
        p.iPos.addUpdateRange(p.instLo * 3, n * 3);
        p.iPos.needsUpdate = true;
        p.iSize.clearUpdateRanges();
        p.iSize.addUpdateRange(p.instLo * 2, n * 2);
        p.iSize.needsUpdate = true;
        p.instLo = -1;
        p.instHiDirty = -1;
      }
      if (p.uvLo >= 0) {
        p.iUv.clearUpdateRanges();
        p.iUv.addUpdateRange(p.uvLo * 4, (p.uvHi - p.uvLo + 1) * 4);
        p.iUv.needsUpdate = true;
        p.uvLo = -1;
        p.uvHi = -1;
      }
    }
    probes.end(P.atlas);
  }

  /**
   * Envia só o retângulo da célula (texSubImage2D). Sem renderer — testes
   * headless — reenvia a página inteira.
   *
   * Na PRIMEIRA escrita de uma página, `needsUpdate` faz o three subir a textura
   * completa a partir do canvas (que já contém a célula recém-pintada); a partir
   * daí só o retângulo é reenviado. O canvas da página segue sendo atualizado a
   * cada escrita justamente para esse caminho: se o contexto WebGL for perdido,
   * o three recria a textura a partir dele.
   */
  _upload(page, cx, cy) {
    const r = this.renderer;
    // Uma falha do upload parcial é PERSISTENTE na prática (driver sem a extensão, ou a
    // janela de contexto perdido de um telão de 8 h). Sem memorizar, cada célula nova
    // tentava de novo, falhava de novo e caía no reenvio da página INTEIRA: 16 MB por
    // nome. MEDIDO num dia de evento (2.937 pinturas de célula), 45,9 GB de upload e o
    // mesmo número de avisos acumulados no console da aba que ninguém está olhando.
    if (!r || !r.copyTextureToTexture || this._semCopiaParcial) { page.tex.needsUpdate = true; return; }
    try {
      if (!page.uploaded) { page.tex.needsUpdate = true; page.uploaded = true; }
      this._scratchTex.needsUpdate = true;
      _dstPos.set(cx, cy);
      r.copyTextureToTexture(this._scratchTex, page.tex, null, _dstPos);
    } catch (err) {
      // driver sem suporte / contexto perdido → volta para o caminho seguro DE VEZ
      this._semCopiaParcial = true;
      console.warn('[LabelAtlas] upload parcial falhou — passando a reenviar a página inteira', err);
      page.tex.needsUpdate = true;
    }
  }
}
